import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, Tag, Percent, RefreshCw, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const SyncStatusBadge = ({ status, error }) => {
  if (status === 'synced') {
    return (
      <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 text-xs" data-testid="sync-status-synced">
        <CheckCircle className="h-3 w-3 mr-1" /> Shopify Synced
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50 text-xs" title={error || 'Sync Failed'} data-testid="sync-status-failed">
        <XCircle className="h-3 w-3 mr-1" /> Sync Failed
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-xs" data-testid="sync-status-pending">
        <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Syncing...
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-gray-500 border-gray-300 text-xs" data-testid="sync-status-none">
      Not Synced
    </Badge>
  );
};

const Offers = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  
  useEffect(() => {
    fetchOffers();
  }, []);
  
  const fetchOffers = async () => {
    try {
      const data = await apiCall('/api/offers/');
      setOffers(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load offers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    
    try {
      await apiCall(`/api/offers/${id}`, { method: 'DELETE' });
      setOffers(offers.filter(o => o.id !== id));
      toast({
        title: 'Success',
        description: 'Offer deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };
  
  const handleSyncToShopify = async (offerId) => {
    setSyncingId(offerId);
    try {
      const result = await apiCall(`/api/offers/${offerId}/sync-shopify`, { method: 'POST' });
      if (result.success) {
        toast({ title: 'Synced', description: 'Offer synced to Shopify' });
      } else {
        toast({ title: 'Sync Failed', description: result.message, variant: 'destructive' });
      }
      fetchOffers();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSyncingId(null);
    }
  };
  
  const getDiscountDisplay = (offer) => {
    if (offer.discount_type === 'percentage') {
      return `${offer.discount_value}% OFF`;
    }
    return `₹${offer.discount_value} OFF`;
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading offers...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Offers & Discounts</h1>
            <p className="text-gray-600 mt-2">Manage promotional offers and coupon codes</p>
          </div>
          <Button onClick={() => navigate('/offers/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Offer
          </Button>
        </div>
        
        {offers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Offers Yet</h3>
              <p className="text-gray-600 mb-4">Create your first offer to boost sales</p>
              <Button onClick={() => navigate('/offers/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Offer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map(offer => (
              <Card key={offer.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`offer-card-${offer.id}`}>
                <div className={`h-2 ${offer.active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl mb-1">{offer.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2">{offer.description}</p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <Badge variant={offer.active ? 'default' : 'secondary'}>
                        {offer.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <SyncStatusBadge status={offer.shopify_sync_status} error={offer.shopify_sync_error} />
                    </div>
                  </div>
                  
                  {offer.shopify_sync_status === 'failed' && offer.shopify_sync_error && (
                    <div
                      className="mb-3 px-3 py-2 rounded-md border border-red-200 bg-red-50 text-xs text-red-800 break-words"
                      data-testid={`sync-error-${offer.id}`}
                    >
                      <span className="font-semibold">Shopify rejected:</span> {offer.shopify_sync_error}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-4 text-center">
                      <Percent className="h-6 w-6 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{getDiscountDisplay(offer)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coupon Code:</span>
                      <code className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">
                        {offer.coupon_code}
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Min Order:</span>
                      <span className="font-medium">₹{offer.min_order_value}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Funded By:</span>
                      <Badge
                        variant="outline"
                        className={
                          offer.funded_by === 'marketplace' ? 'text-purple-700 border-purple-300 bg-purple-50' :
                          offer.funded_by === 'both' ? 'text-amber-700 border-amber-300 bg-amber-50' :
                          'text-blue-700 border-blue-300 bg-blue-50'
                        }
                        data-testid={`funded-by-badge-${offer.id}`}
                      >
                        {offer.funded_by === 'marketplace' ? 'Marketplace' :
                         offer.funded_by === 'both' ? `Both (Vendor ${offer.vendor_funding_percent ?? 50}%)` :
                         'Vendor'}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valid Until:</span>
                      <span className="font-medium">{new Date(offer.end_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Used:</span>
                      <span className="font-medium">
                        {offer.used_count} / {offer.usage_limit || '\u221e'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => navigate(`/offers/edit/${offer.id}`)}
                      data-testid={`edit-offer-${offer.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {(!offer.shopify_sync_status || offer.shopify_sync_status === 'failed' || offer.shopify_sync_status === 'pending') && (
                      <Button
                        variant="outline"
                        className="text-blue-600"
                        onClick={() => handleSyncToShopify(offer.id)}
                        disabled={syncingId === offer.id}
                        data-testid={`sync-offer-${offer.id}`}
                      >
                        {syncingId === offer.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => handleDelete(offer.id)}
                      data-testid={`delete-offer-${offer.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Offers;